import { useEffect, useRef } from 'react'
import * as THREE from 'three'

const NeuralBackground = () => {
  const containerRef = useRef(null)
  const mouseRef = useRef({ x: 0, y: 0 })
  
  useEffect(() => {
    if (!containerRef.current) return
    
    const scene = new THREE.Scene()
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000)
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true })
    
    renderer.setSize(window.innerWidth, window.innerHeight)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    containerRef.current.appendChild(renderer.domElement)
    
    // Particles
    const particlesGeometry = new THREE.BufferGeometry()
    const particlesCount = 60
    const posArray = new Float32Array(particlesCount * 3)
    const velocityArray = []
    
    for(let i = 0; i < particlesCount * 3; i++) {
      posArray[i] = (Math.random() - 0.5) * 20
    }
    
    for(let i = 0; i < particlesCount; i++) {
      velocityArray.push({
        x: (Math.random() - 0.5) * 0.008,
        y: (Math.random() - 0.5) * 0.008,
        z: (Math.random() - 0.5) * 0.008
      })
    }
    
    particlesGeometry.setAttribute('position', new THREE.BufferAttribute(posArray, 3))
    
    const particlesMaterial = new THREE.PointsMaterial({
      size: 0.05,
      color: 0x00d4ff,
      transparent: true,
      opacity: 0.5,
    })
    
    const particlesMesh = new THREE.Points(particlesGeometry, particlesMaterial)
    scene.add(particlesMesh)
    
    const lineMaterial = new THREE.LineBasicMaterial({ 
      color: 0x00d4ff, 
      transparent: true, 
      opacity: 0.08 
    })
    
    camera.position.z = 5
    
    const handleMouseMove = (event) => {
      mouseRef.current.x = (event.clientX / window.innerWidth) * 2 - 1
      mouseRef.current.y = -(event.clientY / window.innerHeight) * 2 + 1
    }
    
    document.addEventListener('mousemove', handleMouseMove, { passive: true })
    
    let animationId
    let frame = 0
    
    function animate() {
      animationId = requestAnimationFrame(animate)
      frame++
      
      const positions = particlesGeometry.attributes.position.array
      
      for(let i = 0; i < particlesCount; i++) {
        const i3 = i * 3
        
        positions[i3] += velocityArray[i].x
        positions[i3 + 1] += velocityArray[i].y
        positions[i3 + 2] += velocityArray[i].z
        
        if (Math.abs(positions[i3]) > 10) velocityArray[i].x *= -1
        if (Math.abs(positions[i3 + 1]) > 10) velocityArray[i].y *= -1
        if (Math.abs(positions[i3 + 2]) > 10) velocityArray[i].z *= -1
      }
      
      particlesGeometry.attributes.position.needsUpdate = true
      
      particlesMesh.rotation.x += 0.0005
      particlesMesh.rotation.y += 0.0005
      particlesMesh.rotation.x += mouseRef.current.y * 0.0005
      particlesMesh.rotation.y += mouseRef.current.x * 0.0005
      
      // Draw connections every 3rd frame for performance
      if (frame % 3 === 0) {
        const linesToRemove = []
        scene.children.forEach((child) => {
          if (child instanceof THREE.Line) linesToRemove.push(child)
        })
        linesToRemove.forEach(line => scene.remove(line))
        
        for(let i = 0; i < particlesCount; i++) {
          let connections = 0
          for(let j = i + 1; j < particlesCount; j++) {
            const dx = positions[i * 3] - positions[j * 3]
            const dy = positions[i * 3 + 1] - positions[j * 3 + 1]
            const dz = positions[i * 3 + 2] - positions[j * 3 + 2]
            const distance = Math.sqrt(dx * dx + dy * dy + dz * dz)
            
            if (distance < 3 && connections < 2) {
              const lineGeometry = new THREE.BufferGeometry().setFromPoints([
                new THREE.Vector3(positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2]),
                new THREE.Vector3(positions[j * 3], positions[j * 3 + 1], positions[j * 3 + 2])
              ])
              const line = new THREE.Line(lineGeometry, lineMaterial)
              scene.add(line)
              connections++
            }
          }
        }
      }
      
      scene.add(particlesMesh)
      renderer.render(scene, camera)
    }
    
    animate()
    
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight
      camera.updateProjectionMatrix()
      renderer.setSize(window.innerWidth, window.innerHeight)
    }
    
    window.addEventListener('resize', handleResize, { passive: true })
    
    return () => {
      cancelAnimationFrame(animationId)
      document.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('resize', handleResize)
      if (containerRef.current && renderer.domElement) {
        containerRef.current.removeChild(renderer.domElement)
      }
      renderer.dispose()
    }
  }, [])
  
  return (
    <div 
      ref={containerRef} 
      className="fixed inset-0 -z-10 opacity-40 pointer-events-none"
    />
  )
}

export default NeuralBackground
